import React, { useState, useEffect } from 'react';
import FileInput, { FileDetails } from '@amzn/meridian/file-input';
import { getItemEntriesValidationError } from '@components/agile/utils/InputItemEntriesValidationUtils';
import { ItemEntryInput } from '@components/agile/types/ItemEntryTypes';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';

export interface ItemBulkUploadInputProps {
  setInputItemEntries: (value: ItemEntryInput[]) => void;
  setInputTableFlag: (value: boolean) => void;
  setReviewTableFlag: (value: boolean) => void;
  setAlertFlag: (value: boolean) => void;
  hasMacroplanogramData: boolean;
  hasMacroplanogramSelected: boolean;
  reviewTableFlag: boolean;
  inputTableFlag: boolean;
  resetKey: number;
}

const ItemBulkUpload = (props: ItemBulkUploadInputProps) => {
  const {
    setInputItemEntries,
    setInputTableFlag,
    setReviewTableFlag,
    setAlertFlag,
    hasMacroplanogramData,
    hasMacroplanogramSelected,
    reviewTableFlag,
    inputTableFlag,
    resetKey,
  } = props;
  const config = useProgramConfig();
  const itemIdentifierLabel = config.labels.itemIdentifierType;
  const subcategoryLabel = config.labels.subcategory;
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const [file, setFile] = useState<File | null>(null);
  const [percentage, setPercentage] = useState<number>(0);
  const [invalidItemEntryErrorFlag, setInvalidItemEntryErrorFlag] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Accept either the canonical column name or the program's configured label
  // (e.g. "scancode"/"asin" + "subcategory"/"velocity"). Matching is case-insensitive.
  // Extra columns (e.g. the category/drop zone column in GFC's template) are
  // discovered but ignored — the active macroplanogram drives routing.
  const itemIdHeaderAliases = new Set(['scancode', 'asin', 'sku', itemIdentifierLabel.toLowerCase()]);
  const subcategoryHeaderAliases = new Set(['subcategory', subcategoryLabel.toLowerCase()]);

  const missingColumnsError =
    scope === 'subcategory'
      ? `CSV must contain ${itemIdentifierLabel} and ${subcategoryLabel} columns.`
      : `CSV must contain a ${itemIdentifierLabel} column.`;

  // Clear file input state when resetKey changes
  useEffect(() => {
    setFile(null);
    setPercentage(0);
    setInvalidItemEntryErrorFlag(false);
    setErrorMessage('');
  }, [resetKey]);

  function parseCsvRow(
    row: string,
    scanCodeColumnIndex: number,
    subcategoryColumnIndex: number,
  ): ItemEntryInput | null {
    const regex = /,(?=(?:[^"']*["'][^"']*["'])*[^"']*$)/g;
    const values = row.split(regex);

    if (scanCodeColumnIndex === -1) return null;
    return {
      itemId: values[scanCodeColumnIndex].trim(),
      itemType: itemIdentifierLabel.toUpperCase(),
      // When subcategory is not required, the column is optional and the
      // downstream flow defaults to the macroplanogram's single subcategory.
      subcategory: subcategoryColumnIndex !== -1 ? values[subcategoryColumnIndex].trim() : '',
    };
  }

  async function parseCSVToEntries(inputFile: File): Promise<ItemEntryInput[]> {
    const fileReader = new FileReader();
    return new Promise((resolve, reject) => {
      fileReader.onload = (event) => {
        if (event.target && event.target.result !== null && event.target.result !== undefined) {
          const fileContent = event.target.result as string;
          if (fileContent.trim().length === 0) {
            reject(new Error('Empty file.'));
            return;
          }
          const rows = fileContent.trim().split(/\r?\n/);
          const itemEntries: ItemEntryInput[] = [];
          const headerRow = rows[0].split(',');
          const scanCodeColumnIndex = headerRow.findIndex((header) =>
            itemIdHeaderAliases.has(header.trim().toLowerCase()),
          );
          const subcategoryColumnIndex = headerRow.findIndex((header) =>
            subcategoryHeaderAliases.has(header.trim().toLowerCase()),
          );

          if (scanCodeColumnIndex === -1 || (scope === 'subcategory' && subcategoryColumnIndex === -1)) {
            reject(new Error(missingColumnsError));
            return;
          }

          // eslint-disable-next-line no-plusplus
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            const entry = parseCsvRow(row, scanCodeColumnIndex, subcategoryColumnIndex);
            if (entry) {
              itemEntries.push(entry);
            }
          }
          resolve(itemEntries);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      fileReader.onerror = () => {
        reject(fileReader.error);
      };

      fileReader.readAsText(inputFile);
    });
  }

  const handleUpload = (selectedFile1: File) => {
    if (selectedFile1 !== null) {
      parseCSVToEntries(selectedFile1)
        .then((entries: ItemEntryInput[]) => {
          const validationError = getItemEntriesValidationError(entries);
          if (!validationError) {
            setInputItemEntries(entries);
            setReviewTableFlag(true);
            setInvalidItemEntryErrorFlag(false);
            setErrorMessage('');
          } else {
            setInvalidItemEntryErrorFlag(true);
            setErrorMessage(validationError);
          }
          return entries;
        })
        .catch((error: Error) => {
          console.error('CSV upload failed while parsing CSV', error);
          setInvalidItemEntryErrorFlag(true);
          setErrorMessage(error.message);
        });
    }
  };

  const onFileAttached = (acceptedFiles: File[]) => {
    // Check if store, date, and macroplanogram are selected
    if (!hasMacroplanogramData || !hasMacroplanogramSelected) {
      setAlertFlag(true);
      return;
    }

    setInputTableFlag(false);
    const newFile = acceptedFiles[0];
    setFile(newFile);
    handleUpload(newFile);
    setPercentage(100);
  };

  const onClickCancel = () => {
    setFile(null);
    setPercentage(0);
    setInputTableFlag(false);
    setReviewTableFlag(false);
    setInputItemEntries([]);
    setInvalidItemEntryErrorFlag(false);
    setErrorMessage('');
  };

  const onClickRemoveFile = () => {
    setFile(null);
    setPercentage(0);
    setInputTableFlag(false);
    setReviewTableFlag(false);
    setInputItemEntries([]);
    setInvalidItemEntryErrorFlag(false);
    setErrorMessage('');
  };

  return (
    <>
      <FileInput
        type="single"
        onFileAttached={onFileAttached}
        accept="text/csv"
        uploadButtonDisabled={inputTableFlag || reviewTableFlag}
        data-testid="item-bulk-input-file-input"
      />
      {file && (
        <FileDetails
          file={file}
          onClickCancel={onClickCancel}
          onClickRemoveFile={onClickRemoveFile}
          cancelUploadButtonLabel={`Cancel, ${file.name}`}
          removeFileButtonLabel={`Remove, ${file.name}`}
          uploadPercentage={percentage}
          uploadComplete={percentage === 100}
          error={invalidItemEntryErrorFlag}
          errorMessage={errorMessage}
        />
      )}
    </>
  );
};

export default ItemBulkUpload;
